import React from 'react';
import Box from '@mui/material/Box';
import { AgedChip } from './TierChip';
import { EvidenceSourceTag } from '../../design/primitives';

export type EvidenceSourceType = 'calendar' | 'metrics' | 'pr' | 'doc' | 'meeting' | 'experiment' | 'ticket' | 'pipeline';

export interface EvidenceSource {
  type: EvidenceSourceType | string;
  detail: string;
  meta?: Record<string, any>;
  /** Weak evidence cannot carry a tier alone: a ticket, one comment, an unconfirmed edge, a movement below the window. */
  weak?: boolean;
}

const LABEL: Record<string, string> = {
  calendar: 'calendar', metrics: 'metrics', pr: 'PR', doc: 'doc', meeting: 'meeting', experiment: 'experiment', ticket: 'ticket', pipeline: 'pipeline',
};

const clip = (s: string, n = 56) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** The typed sources behind a driver (#185), weak ones dimmed so weak evidence looks weak. */
export default function EvidenceStrip({ sources, max = 4 }: { sources: EvidenceSource[] | null | undefined; max?: number }) {
  const list = (sources || []).slice(0, max);
  if (list.length === 0) return null;
  const more = (sources || []).length - list.length;
  return (
    <Box data-testid="evidence-strip" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {list.map((s, i) => (
        <React.Fragment key={i}><EvidenceSourceTag
          weak={s.weak}
          size="small"
          variant="outlined"
          title={s.detail}
          label={`${LABEL[s.type] || s.type}: ${clip(s.detail)}`}
          data-testid="evidence-chip"
          data-type={s.type}
          data-weak={s.weak ? 'true' : 'false'}
          sx={{ height: 20, fontSize: '0.68rem', maxWidth: 320 }}
        />{s.meta?.aged && <AgedChip days={s.meta.decay_days} />}</React.Fragment>
      ))}
      {more > 0 && <Box component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>+{more} more</Box>}
    </Box>
  );
}
